import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from '../Gallery/Modal';
import BrandLogo from '../BrandLogo';
import SocialLinks from '../Common/SocialLinks';
import { api, getListItems } from '../../services/api';
import { useSiteContent, sectionField } from '../../hooks/useSiteContent';

function isInternalPath(url) {
    return typeof url === 'string' && url.startsWith('/') && !url.startsWith('//');
}

function FooterNavLink({ to, children }) {
    if (!to) return null;
    if (isInternalPath(to)) {
        return <Link to={to}>{children}</Link>;
    }
    return (
        <a href={to} target="_blank" rel="noopener noreferrer">
            {children}
        </a>
    );
}

function FooterFour() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalImage, setModalImage] = useState('');
    const [email, setEmail] = useState('');
    const [newsStatus, setNewsStatus] = useState({ type: '', text: '' });
    const [subscribing, setSubscribing] = useState(false);
    const [settings, setSettings] = useState({});
    const [igImages, setIgImages] = useState([]);
    const { content: siteContent } = useSiteContent('site');
    const igTitle = sectionField(siteContent, 'footer', 'instagram_title', 'From Instagram');
    const newsPlaceholder = sectionField(siteContent, 'footer', 'newsletter_placeholder', 'Your email');
    const newsButton = sectionField(siteContent, 'footer', 'newsletter_button', 'Subscribe');

    useEffect(() => {
        let cancelled = false;
        api.getSettings()
            .then((res) => {
                if (!cancelled) setSettings(res.data || {});
            })
            .catch(() => {});
        api.getGallery('footer_instagram')
            .then((res) => {
                if (cancelled) return;
                const items = getListItems(res).map((item) => item.image).filter(Boolean);
                setIgImages(items);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const quickLinks = useMemo(() => {
        const links = [];
        for (let i = 1; i <= 5; i += 1) {
            const label = (settings[`footer_link_${i}_label`] || '').trim();
            const url = (settings[`footer_link_${i}_url`] || '').trim();
            if (label && url) links.push({ label, url });
        }
        return links;
    }, [settings]);

    const phones = useMemo(
        () => [settings.footer_phone_1, settings.footer_phone_2].map((v) => (v || '').trim()).filter(Boolean),
        [settings]
    );
    const emails = useMemo(
        () => [settings.footer_email_1, settings.footer_email_2].map((v) => (v || '').trim()).filter(Boolean),
        [settings]
    );

    const aboutText = (settings.footer_about_text || '').trim()
        || 'We help you explore new places with guided tours and simple, memorable trips. Travel With Ammar is here for your next nature getaway.';
    const newsletterTitle = (settings.footer_newsletter_title || '').trim()
        || 'Stay in the loop with our latest trips';
    const linksTitle = (settings.footer_links_title || '').trim() || 'Explore';
    const contactTitle = (settings.footer_contact_title || '').trim() || 'Talk to us';
    const address = (settings.footer_address || '').trim();
    const copyrightText = (settings.footer_copyright || '').trim()
        || 'Travel With Ammar. All rights reserved.';

    const openModal = (imageSrc, event) => {
        event.preventDefault();
        setModalImage(imageSrc);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const handleNewsletter = async (e) => {
        e.preventDefault();
        setSubscribing(true);
        setNewsStatus({ type: '', text: '' });
        try {
            await api.subscribeNewsletter({ email });
            setNewsStatus({ type: 'success', text: 'Subscribed successfully!' });
            setEmail('');
        } catch (err) {
            setNewsStatus({ type: 'error', text: err.message || 'Subscription failed' });
        } finally {
            setSubscribing(false);
        }
    };

    return (
        <footer className="footer-wrapper bg-title footer-layout2 shape-mockup-wrap twa-footer">
            <div className="widget-area">
                <div className="container">
                    <div className="newsletter-area">
                        <div className="newsletter-top">
                            <div className="row gy-3 align-items-center">
                                <div className="col-lg-5">
                                    <h2 className="newsletter-title text-white text-capitalize mb-0">
                                        {newsletterTitle}
                                    </h2>
                                </div>
                                <div className="col-lg-7">
                                    <form className="newsletter-form style2" onSubmit={handleNewsletter}>
                                        <input
                                            className="form-control"
                                            type="email"
                                            placeholder={newsPlaceholder}
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                        />
                                        <button type="submit" className="th-btn style1" disabled={subscribing}>
                                            {subscribing ? 'Sending…' : newsButton}{' '}
                                            <img src="/assets/img/icon/plane2.svg" alt="" />
                                        </button>
                                    </form>
                                    {newsStatus.text && (
                                        <p className={`mb-0 mt-2 ${newsStatus.type === 'error' ? 'text-danger' : 'text-success'}`}>
                                            {newsStatus.text}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row justify-content-between twa-footer-cols">
                        <div className="col-12 col-md-6 col-xl-3">
                            <div className="widget footer-widget">
                                <div className="th-widget-about">
                                    <div className="about-logo">
                                        <BrandLogo variant="light" />
                                    </div>
                                    <p className="about-text">{aboutText}</p>
                                    <div className="th-social">
                                        <SocialLinks className="" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {quickLinks.length > 0 && (
                            <div className="col-12 col-md-6 col-xl-auto">
                                <div className="widget widget_nav_menu footer-widget">
                                    <h3 className="widget_title">{linksTitle}</h3>
                                    <div className="menu-all-pages-container">
                                        <ul className="menu">
                                            {quickLinks.map((link) => (
                                                <li key={`${link.label}-${link.url}`}>
                                                    <FooterNavLink to={link.url}>{link.label}</FooterNavLink>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="col-12 col-md-6 col-xl-auto">
                            <div className="widget footer-widget">
                                <h3 className="widget_title">{contactTitle}</h3>
                                <div className="th-widget-contact">
                                    {phones.length > 0 && (
                                        <div className="info-box_text">
                                            <div className="icon">
                                                <img src="/assets/img/icon/phone.svg" alt="img" />
                                            </div>
                                            <div className="details">
                                                {phones.map((phone) => (
                                                    <p key={phone}>
                                                        <a href={`tel:${phone.replace(/\s+/g, '')}`} className="info-box_link">
                                                            {phone}
                                                        </a>
                                                    </p>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {emails.length > 0 && (
                                        <div className="info-box_text">
                                            <div className="icon">
                                                <img src="/assets/img/icon/envelope.svg" alt="img" />
                                            </div>
                                            <div className="details">
                                                {emails.map((mail) => (
                                                    <p key={mail}>
                                                        <a href={`mailto:${mail}`} className="info-box_link">
                                                            {mail}
                                                        </a>
                                                    </p>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {address && (
                                        <div className="info-box_text">
                                            <div className="icon">
                                                <img src="/assets/img/icon/location-dot.svg" alt="img" />
                                            </div>
                                            <div className="details">
                                                <p>{address}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {igImages.length > 0 && (
                        <div className="col-12 col-md-6 col-xl-auto">
                            <div className="widget footer-widget">
                                <h3 className="widget_title">{igTitle}</h3>
                                <div className="sidebar-gallery">
                                    {igImages.map((src, idx) => (
                                        <div className="gallery-thumb" key={`${src}-${idx}`}>
                                            <img
                                                src={src}
                                                alt={`Instagram ${idx + 1}`}
                                                onClick={(e) => openModal(src, e)}
                                            />
                                            <Link
                                                to={src}
                                                className="gallery-btn popup-image"
                                                onClick={(e) => openModal(src, e)}
                                            >
                                                <i className="fab fa-instagram" />
                                            </Link>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="copyright-wrap">
                <div className="container">
                    <div className="row justify-content-center align-items-center">
                        <div className="col-12 text-center">
                            <p className="copyright-text">
                                Copyright {new Date().getFullYear()} {copyrightText}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div
                className="shape-mockup movingX d-none d-xxl-block"
                style={{ top: '24%', left: '5%' }}
            >
                <img src="/assets/img/shape/shape_8.png" alt="shape" />
            </div>
            <Modal isOpen={isModalOpen} closeModal={closeModal} imageSrc={modalImage} />
        </footer>
    );
}

export default FooterFour;
